import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { api, apiError } from "../lib/api";
import { COMPANY, waLink, mailLink } from "../data/site";
import { Reveal, Overline, PageHeader } from "../components/Reveal";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };
const inputCls =
  "w-full py-3.5 px-4 bg-surface border border-border rounded-xl text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition-all";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const waMsg = "Bonjour MS Énergie, je vous contacte via votre site.";

  return (
    <div data-testid="contact-page">
      <PageHeader overline="Contact" title="Parlons de votre projet" subtitle="Une question, un devis, un dépannage ? Notre équipe vous répond rapidement." />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="space-y-4">
            {[
              { icon: Phone, t: "Téléphone", v: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
              { icon: Mail, t: "Email", v: COMPANY.email, href: mailLink("Demande via le site MS Énergie") },
              { icon: MapPin, t: "Adresse", v: `${COMPANY.address}, ${COMPANY.zip} ${COMPANY.city}` },
              { icon: Clock, t: "Horaires", v: COMPANY.hours },
            ].map((c) => (
              <a key={c.t} href={c.href} className={`flex items-start gap-4 bg-white border border-border rounded-2xl p-5 ${c.href ? "hover:border-brand transition-colors" : ""}`}>
                <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center shrink-0"><c.icon className="w-6 h-6 text-brand" /></div>
                <div>
                  <p className="text-text-muted text-xs uppercase tracking-wider font-bold">{c.t}</p>
                  <p className="text-navy font-bold mt-0.5">{c.v}</p>
                </div>
              </a>
            ))}
            <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 bg-[#25D366] text-white rounded-2xl p-5 hover:opacity-95 transition-opacity">
              <MessageCircle className="w-7 h-7" />
              <span className="font-bold">Discuter sur WhatsApp</span>
            </a>

            <div className="rounded-2xl overflow-hidden border border-border h-72 mt-2">
              <iframe
                title="Localisation MS Énergie"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src={`https://www.google.com/maps?q=${COMPANY.mapsQuery}&output=embed`}
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-border rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)] space-y-5">
            <h2 className="font-display text-2xl font-extrabold text-navy">Envoyez-nous un message</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <input required value={form.name} onChange={set("name")} data-testid="contact-name" className={inputCls} placeholder="Nom complet *" />
              <input required value={form.phone} onChange={set("phone")} data-testid="contact-phone" className={inputCls} placeholder="Téléphone" />
            </div>
            <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} placeholder="Email *" />
            <input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className={inputCls} placeholder="Sujet" />
            <textarea required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" className={`${inputCls} resize-none`} placeholder="Votre message *" />
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading ? "Envoi…" : <>Envoyer <Send className="w-4 h-4" /></>}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
